import React, { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { PageHeader, StatusBadge } from "../components/UI";
import { Package, CheckCircle2, Truck, Clock, IndianRupee, TrendingUp } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell, Legend, CartesianGrid
} from "recharts";
import { Link } from "react-router-dom";

const STAT_CARDS = [
  { key: "total_shipments", label: "Total Shipments", icon: Package, color: "bg-blue-500" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, color: "bg-emerald-500" },
  { key: "in_transit", label: "In Transit", icon: Truck, color: "bg-indigo-500" },
  { key: "pending", label: "Pending", icon: Clock, color: "bg-amber-500" },
];

const PIE_COLORS = ["#1E3A8A", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#6366F1", "#94A3B8"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard-stats").then(r => setStats(r.data)); }, []);

  if (!stats) return <Layout><div className="text-slate-500">Loading dashboard...</div></Layout>;

  return (
    <Layout>
      <PageHeader title="Dashboard" subtitle="Operational overview of your logistics" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {STAT_CARDS.map(s => (
          <div key={s.key} className="lt-card lt-stat p-4 sm:p-5 flex items-center gap-3 sm:gap-4 min-w-0" data-testid={`stat-${s.key}`}>
            <div className={`${s.color} w-10 h-10 sm:w-12 sm:h-12 rounded-lg grid place-items-center text-white flex-shrink-0`}><s.icon size={20}/></div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-slate-900 truncate">{stats[s.key]}</div>
              <div className="text-[11px] sm:text-xs text-slate-500 truncate">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lt-card p-4 sm:p-5 lg:col-span-2 min-w-0">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-semibold text-slate-800 text-sm sm:text-base">Monthly Performance</h3>
            <div className="text-[11px] sm:text-xs text-slate-500 flex items-center gap-1"><TrendingUp size={14}/> <span className="hidden sm:inline">Last 12 months</span></div>
          </div>
          <div className="w-full" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={stats.monthly_series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="#F1F5F9" />
                <XAxis dataKey="month" tick={{fontSize:10}}/>
                <YAxis tick={{fontSize:10}} width={40}/>
                <Tooltip />
                <Legend wrapperStyle={{fontSize:11}}/>
                <Line type="monotone" dataKey="shipments" stroke="#1E3A8A" strokeWidth={2.5} name="Shipments"/>
                <Line type="monotone" dataKey="revenue" stroke="#0EA5E9" strokeWidth={2} name="Revenue (₹)"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lt-card p-4 sm:p-5 min-w-0">
          <h3 className="font-semibold text-slate-800 mb-3 text-sm sm:text-base">Status Distribution</h3>
          <div className="w-full" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={stats.status_distribution} dataKey="count" nameKey="status" outerRadius={75} label={(e)=>e.status}>
                  {stats.status_distribution.map((_, i)=> <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lt-card p-5">
          <h3 className="font-semibold text-slate-800 mb-2">Revenue</h3>
          <div className="flex items-baseline gap-1">
            <IndianRupee size={20} className="text-emerald-600"/>
            <span className="text-3xl font-bold text-slate-900" data-testid="stat-revenue">{stats.monthly_revenue.toLocaleString("en-IN")}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Total recorded across all shipments</p>
          <div className="mt-4">
            <ResponsiveContainer width="100%" height={110}>
              <BarChart data={stats.monthly_series}>
                <Bar dataKey="revenue" fill="#0EA5E9" />
                <XAxis dataKey="month" tick={{fontSize:10}}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lt-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Recent Shipments</h3>
            <Link to="/app/consignments" className="text-xs text-sky-700 hover:underline">View all →</Link>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b">
              <tr><th className="text-left py-2">LR Number</th><th className="text-left">Route</th><th className="text-left">Status</th><th className="text-right">Amount</th></tr>
            </thead>
            <tbody data-testid="recent-shipments">
              {stats.recent_shipments.map(s => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2 font-medium text-slate-800">{s.lr_number}</td>
                  <td className="text-slate-600">{s.origin_branch_name} → {s.destination_branch_name}</td>
                  <td><StatusBadge status={s.status}/></td>
                  <td className="text-right text-slate-700">₹{(s.total_amount||0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {stats.recent_shipments.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No shipments yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
